import { m } from '@hub/messages';
import {
  Button,
  cn,
  NotificationBanner,
  NotificationBannerAction,
  NotificationBannerDescription,
  NotificationBannerTitle,
} from '@platform/ui';
import { useBannerActions, useBanners } from './hooks/useBanners';

interface BannerStackProps {
  scope?: 'global' | 'route';
  className?: string;
}

const ACTION_BUTTON_VARIANT = {
  error: 'destructive',
  warning: 'warning',
  info: 'default',
} as const;

export function BannerStack({ scope = 'global', className }: BannerStackProps) {
  const banners = useBanners(scope);
  const { dismissBanner } = useBannerActions();

  if (banners.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {banners.map((banner) => (
        <NotificationBanner
          key={banner.id}
          variant={banner.variant}
          onDismiss={banner.persistent ? undefined : () => dismissBanner(banner.id)}
          dismissLabel={m.banners_common_dismiss()}
        >
          {banner.title ? <NotificationBannerTitle>{banner.title}</NotificationBannerTitle> : null}
          <NotificationBannerDescription>{banner.description}</NotificationBannerDescription>
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
            {banner.action?.map(({ id, label, onClick, variant, className: actionClassName, ariaLabel, ...rest }) => (
              <NotificationBannerAction key={id}>
                <Button
                  variant={variant ?? ACTION_BUTTON_VARIANT[banner.variant]}
                  onClick={onClick}
                  className={cn('w-full sm:w-auto', actionClassName)}
                  aria-label={ariaLabel}
                  {...rest}
                >
                  {label}
                </Button>
              </NotificationBannerAction>
            ))}
          </div>
        </NotificationBanner>
      ))}
    </div>
  );
}
